/**
 * <predictive-search> wraps the search form inside the search panel (snippets/search-modal.liquid).
 *
 * Always: focuses the input when the panel opens, drives the "Clear" button and resets when the panel closes.
 * Unless data-enabled="false": fetches sections/predictive-search.liquid while the shopper types and lets the
 * input move through the results (role="option") with the arrow keys. Without it the form is a plain GET search.
 * Result counts are read to screen readers through theme.utils.announce.
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        if (this.form) return;

        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.defaults = this.querySelector('[data-predictive-default]');
        this.clearButton = this.querySelector('[data-predictive-clear]');
        this.modal = this.closest('theme-modal');
        this.enabled = this.dataset.enabled !== 'false';
        this.cache = new Map();
        this.controller = null;
        this.active = null;
        this.search = theme.utils.debounce(() => this.fetchResults(), 250);

        this.input.addEventListener('input', () => this.onInput());
        this.input.addEventListener('keydown', (event) => this.onKeydown(event));
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.clearButton.addEventListener('click', () => {
          this.reset();
          this.input.focus();
        });

        if (this.modal) {
          this.modal.addEventListener('modal:open', () => this.focusInput());
          this.modal.addEventListener('modal:close', () => this.reset());
        }
      }

      get options() {
        return Array.from(this.results.querySelectorAll('[role="option"]'));
      }

      // The modal already focuses the [autofocus] input; this keeps it true whatever the modal does:
      // right away (inside the tap gesture, so phones open the keyboard) and again on the next frame.
      // preventScroll: the panel is still sliding in from above the viewport.
      focusInput() {
        this.input.focus({ preventScroll: true });
        requestAnimationFrame(() => this.input.focus({ preventScroll: true }));
      }

      onInput() {
        this.clearButton.hidden = this.input.value === '';
        if (!this.enabled) return;

        if (this.input.value.trim()) {
          this.search();
        } else {
          this.clearResults();
        }
      }

      onSubmit(event) {
        if (this.input.value.trim()) return;
        event.preventDefault();
        this.input.focus();
      }

      onKeydown(event) {
        // First Escape dismisses the results (standard combobox behaviour); with nothing showing
        // the key reaches <theme-modal>, which closes the panel and resets this component.
        if (event.key === 'Escape') {
          if (this.results.hidden) return;
          event.preventDefault();
          event.stopPropagation();
          this.reset();
          return;
        }

        const options = this.options;
        if (!options.length) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          const index = options.indexOf(this.active);
          const next = index === -1 && step === -1 ? options.length - 1 : (index + step + options.length) % options.length;
          this.setActive(options[next]);
        } else if (event.key === 'Enter' && this.active) {
          event.preventDefault();
          this.active.click();
        }
      }

      setActive(option) {
        if (this.active) this.active.setAttribute('aria-selected', 'false');
        this.active = option;

        if (option) {
          option.setAttribute('aria-selected', 'true');
          option.scrollIntoView({ block: 'nearest' });
          this.input.setAttribute('aria-activedescendant', option.id);
        } else {
          this.input.removeAttribute('aria-activedescendant');
        }
      }

      setLoading(loading) {
        this.classList.toggle('is-loading', loading);
        this.results.setAttribute('aria-busy', String(loading));
      }

      async fetchResults() {
        const query = this.input.value.trim();
        if (this.controller) this.controller.abort();
        if (!query) return;

        if (this.cache.has(query)) {
          this.render(this.cache.get(query));
          return;
        }

        const controller = new AbortController();
        this.controller = controller;
        this.setLoading(true);

        try {
          const params = new URLSearchParams({
            q: query,
            'resources[type]': 'query,product,collection,page,article',
            'resources[limit]': '6',
            'resources[limit_scope]': 'each',
            section_id: 'predictive-search'
          });
          const response = await fetch(`${this.dataset.url}?${params}`, { signal: controller.signal });
          if (!response.ok) throw new Error(response.statusText);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-predictive-content]');
          const markup = content ? content.outerHTML : '';

          this.cache.set(query, markup);
          this.render(markup);
        } catch (error) {
          // A failed request simply leaves the plain search form; aborted ones were replaced by a newer query.
          if (error.name !== 'AbortError') this.clearResults();
        } finally {
          if (this.controller === controller) this.setLoading(false);
        }
      }

      render(markup) {
        this.results.innerHTML = markup;
        this.results.hidden = !markup;
        this.setActive(null);

        const hasOptions = this.options.length > 0;
        this.input.setAttribute('aria-expanded', String(hasOptions));
        // Keep the quick links visible under the "no results" message.
        if (this.defaults) this.defaults.hidden = hasOptions;

        const content = this.results.firstElementChild;
        if (content && content.dataset.status) theme.utils.announce(content.dataset.status);
      }

      clearResults() {
        if (!this.enabled) return;
        if (this.controller) this.controller.abort();

        this.setLoading(false);
        this.results.innerHTML = '';
        this.results.hidden = true;
        this.setActive(null);
        this.input.setAttribute('aria-expanded', 'false');
        if (this.defaults) this.defaults.hidden = false;
      }

      reset() {
        this.input.value = '';
        this.clearButton.hidden = true;
        this.clearResults();
      }
    }
  );
}
